import { useNavigate } from "react-router-dom";
import { useState, useEffect, useCallback } from "react";
import { 
  Car, 
  ClipboardList, 
  LogOut,
  User as UserIcon,
  Clock,
  ArrowRight,
  Search,
  History,
  Plus,
  X
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import axios from "axios";
import { API_URL } from "../utils/api-config";

const ManagerDashboard = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  // Recherche rapide véhicule
  const [searchPlate, setSearchPlate] = useState("");
  const [searchResults, setSearchResults] = useState<Array<{plate: string; brand?: string; model?: string}>>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showSearchResults, setShowSearchResults] = useState(false);

  // Fonction de recherche de véhicule par plaque
  const searchVehicle = useCallback(async (query: string) => {
    if (query.length < 2) {
      setSearchResults([]);
      setShowSearchResults(false);
      return;
    }
    
    setIsSearching(true);
    try {
      const { data } = await axios.get(`${API_URL}/vehicles?search=${encodeURIComponent(query.toUpperCase())}`);
      const vehicles = Array.isArray(data) ? data : data.vehicles || [];
      const filtered = vehicles
        .filter((v: any) => v.plate?.toUpperCase().includes(query.toUpperCase()))
        .slice(0, 5)
        .map((v: any) => ({
          plate: v.plate,
          brand: v.brand,
          model: v.vehicleModel || v.model,
        }));
      setSearchResults(filtered);
      setShowSearchResults(filtered.length > 0 || query.length >= 2);
    } catch (err) {
      console.error("Erreur recherche véhicule:", err);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  }, []);

  // Debounce pour la recherche
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchPlate) {
        searchVehicle(searchPlate);
      } else {
        setSearchResults([]);
        setShowSearchResults(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchPlate, searchVehicle]);

  const handleSelectVehicle = (plate: string, action: "history" | "assignment") => {
    setSearchPlate("");
    setShowSearchResults(false);
    if (action === "history") {
      navigate(`/vehicules/historique/${plate}`);
    } else {
      navigate(`/assignments/new?plate=${plate}`);
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const menuItems = [
    {
      title: "Historique des véhicules",
      description: "Consulter l'historique complet du parc automobile",
      icon: Clock,
      link: "/historique-vehicules",
      gradient: "from-orange-500 to-amber-600",
      hoverGradient: "hover:from-orange-600 hover:to-amber-700",
      iconBg: "bg-orange-100",
      iconColor: "text-orange-600"
    },
    {
      title: "Affectations",
      description: "Gérer et suivre les affectations de véhicules",
      icon: ClipboardList,
      link: "/assignments",
      gradient: "from-emerald-500 to-teal-600",
      hoverGradient: "hover:from-emerald-600 hover:to-teal-700",
      iconBg: "bg-emerald-100",
      iconColor: "text-emerald-600"
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
      {/* Header */}
      <div className="bg-white/90 backdrop-blur-md border-b border-gray-200 shadow-sm sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-gradient-to-br from-indigo-600 to-purple-600 rounded-xl shadow-lg">
                <Car className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900">
                  Espace Manager
                </h1>
                <p className="text-sm text-gray-500" style={{ fontFamily: '"Bauhaus 93", "Orbitron", sans-serif' }}>
                  Master<span style={{ color: '#64B5F6' }}>D</span>i Fleet
                </p>
              </div>
            </div>

            {user && (
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-gray-50 to-indigo-50 rounded-lg border border-gray-200">
                  <UserIcon className="w-4 h-4 text-indigo-600" />
                  <div className="text-sm">
                    <p className="font-medium text-gray-900">
                      {user.firstName || user.username}
                    </p>
                    <p className="text-xs text-gray-500">{user.role || "Manager"}</p>
                  </div>
                </div>
                <button
                  onClick={handleLogout}
                  className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-all shadow-sm hover:shadow"
                >
                  <LogOut className="w-4 h-4" />
                  <span className="hidden sm:inline">Déconnexion</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Welcome Section */}
        <div className="text-center mb-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-3">
            Bienvenue{user?.firstName ? `, ${user.firstName}` : ""} 👋
          </h2>
          <p className="text-gray-600 text-lg">
            Sélectionnez une option pour commencer
          </p>
        </div>

        {/* Recherche rapide véhicule */}
        <div className="mb-10 relative">
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100">
            <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-4 rounded-t-2xl">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-white/20 rounded-lg">
                  <Search className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white">Recherche rapide</h3>
                  <p className="text-sm text-white/80">Tapez une plaque pour accéder directement au véhicule</p>
                </div>
              </div>
            </div>
            <div className="p-6">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Entrez une plaque d'immatriculation (ex: AB-123-CD)..."
                  value={searchPlate}
                  onChange={(e) => setSearchPlate(e.target.value.toUpperCase())}
                  className="w-full px-4 py-4 pl-12 text-lg font-mono rounded-xl border-2 border-gray-200 bg-gray-50 text-gray-900 placeholder-gray-400 focus:outline-none focus:border-indigo-500 focus:bg-white focus:ring-4 focus:ring-indigo-100 transition-all"
                  style={{ letterSpacing: "0.1em" }}
                />
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                {searchPlate && (
                  <button
                    onClick={() => {
                      setSearchPlate("");
                      setShowSearchResults(false);
                    }}
                    className="absolute right-4 top-1/2 -translate-y-1/2 p-1.5 hover:bg-gray-200 rounded-full transition-colors"
                  >
                    <X className="w-4 h-4 text-gray-500" />
                  </button>
                )}
              </div>
              <p className="mt-3 text-sm text-gray-500 flex items-center gap-2">
                <span className="text-lg">💡</span>
                Tapez au moins 2 caractères pour rechercher un véhicule
              </p>
            </div>
          </div>
          
          {/* Résultats de recherche - en dehors du conteneur principal */}
          {showSearchResults && searchResults.length > 0 && (
            <div className="absolute left-0 right-0 mt-2 bg-white rounded-xl shadow-2xl border border-gray-200 z-50 max-h-[350px] overflow-y-auto">
              {searchResults.map((vehicle) => (
                <div
                  key={vehicle.plate}
                  className="p-4 hover:bg-indigo-50 border-b border-gray-100 last:border-0 transition-colors"
                >
                  <div className="flex items-center gap-3 mb-3">
                    <div className="p-2 bg-indigo-100 rounded-lg flex-shrink-0">
                      <Car className="w-5 h-5 text-indigo-600" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="font-mono font-bold text-gray-900 text-base tracking-wider truncate">{vehicle.plate}</p>
                      <p className="text-sm text-gray-500 truncate">{vehicle.brand} {vehicle.model}</p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleSelectVehicle(vehicle.plate, "history")}
                      className="flex-1 flex items-center justify-center gap-2 px-3 py-2.5 bg-gradient-to-r from-orange-500 to-amber-600 text-white rounded-lg hover:from-orange-600 hover:to-amber-700 transition-all font-medium text-sm shadow-sm hover:shadow-md"
                    >
                      <History className="w-4 h-4" />
                      <span>Historique</span>
                    </button>
                    <button
                      onClick={() => handleSelectVehicle(vehicle.plate, "assignment")}
                      className="flex-1 flex items-center justify-center gap-2 px-3 py-2.5 bg-gradient-to-r from-emerald-500 to-teal-600 text-white rounded-lg hover:from-emerald-600 hover:to-teal-700 transition-all font-medium text-sm shadow-sm hover:shadow-md"
                    >
                      <Plus className="w-4 h-4" />
                      <span>Affectation</span>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
          
          {/* Message si recherche en cours */}
          {isSearching && (
            <div className="absolute left-0 right-0 mt-2 bg-white rounded-xl shadow-lg border border-gray-200 p-4 text-center z-50">
              <div className="flex items-center justify-center gap-2 text-gray-500">
                <div className="w-5 h-5 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
                Recherche en cours...
              </div>
            </div>
          )}
          
          {/* Message si aucun résultat */}
          {searchPlate.length >= 2 && !isSearching && searchResults.length === 0 && showSearchResults && (
            <div className="absolute left-0 right-0 mt-2 bg-white rounded-xl shadow-lg border border-gray-200 p-4 text-center z-50">
              <p className="text-gray-500">Aucun véhicule trouvé pour "<span className="font-mono font-bold">{searchPlate}</span>"</p>
            </div>
          )}
        </div>

        {/* Menu Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {menuItems.map((item) => {
            const Icon = item.icon;
            return (
              <div
                key={item.title}
                onClick={() => navigate(item.link)}
                className="group relative bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden cursor-pointer transform transition-all duration-300 hover:scale-[1.02] hover:shadow-2xl"
              >
                {/* Gradient Overlay on Hover */}
                <div className={`absolute inset-0 bg-gradient-to-br ${item.gradient} opacity-0 group-hover:opacity-5 transition-opacity duration-300`}></div>
                
                <div className="relative p-8">
                  {/* Icon */}
                  <div className={`inline-flex p-4 rounded-2xl ${item.iconBg} mb-6 group-hover:scale-110 transition-transform duration-300`}>
                    <Icon className={`w-8 h-8 ${item.iconColor}`} />
                  </div>

                  {/* Title & Description */}
                  <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-gray-700 transition-colors">
                    {item.title}
                  </h3>
                  <p className="text-gray-600 mb-6">
                    {item.description}
                  </p>

                  {/* Action Button */}
                  <div className={`inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r ${item.gradient} ${item.hoverGradient} text-white rounded-xl font-medium transition-all duration-200 shadow-md group-hover:shadow-lg`}>
                    <span>Accéder</span>
                    <ArrowRight className="w-4 h-4 transform group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>

                {/* Bottom Border Accent */}
                <div className={`h-1 bg-gradient-to-r ${item.gradient} transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left`}></div>
              </div>
            );
          })}
        </div>

        {/* Footer Info */}
        <div className="mt-12 text-center">
          <p className="text-sm text-gray-500">
            Besoin d'aide ? Contactez l'administrateur système
          </p>
        </div>
      </div>
    </div>
  );
};

export default ManagerDashboard;
